import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { db } from '../firebase';
import { useApp } from './AppContext';

// Default Pomodoro settings
const DEFAULT_SETTINGS = {
  workDuration: 25,
  shortBreakDuration: 5,
  longBreakDuration: 15,
  sessionsBeforeLongBreak: 4,
  autoStartBreaks: false,
  autoStartWork: false,
  soundEnabled: true,
  notificationsEnabled: true,
};

// Timer modes
export const TIMER_MODES = {
  WORK: 'work',
  SHORT_BREAK: 'shortBreak',
  LONG_BREAK: 'longBreak',
};

// Mode labels
export const MODE_LABELS = {
  [TIMER_MODES.WORK]: 'Focus',
  [TIMER_MODES.SHORT_BREAK]: 'Short break',
  [TIMER_MODES.LONG_BREAK]: 'Long break',
};

// Mode colors
export const MODE_COLORS = {
  [TIMER_MODES.WORK]: 'textPrimary',
  [TIMER_MODES.SHORT_BREAK]: 'accent',
  [TIMER_MODES.LONG_BREAK]: 'info',
};

// MARK: - Live Activity Bridge (iOS Dynamic Island)
const postToNative = (action, data = {}) => {
  if (Capacitor.isNativePlatform() && window.webkit?.messageHandlers?.nativeHeader) {
    window.webkit.messageHandlers.nativeHeader.postMessage({ action, ...data });
  }
};

const startLiveActivity = (mode, totalDuration, taskTitle, timeRemaining) => {
  postToNative('startLiveActivity', { mode, totalDuration, taskTitle, timeRemaining });
};

const updateLiveActivity = (timeRemaining, isRunning, mode) => {
  postToNative('updateLiveActivity', { timeRemaining, isRunning, mode });
};

const pauseLiveActivity = (timeRemaining, mode) => {
  postToNative('pauseLiveActivity', { timeRemaining, mode });
};

const endLiveActivity = () => {
  postToNative('endLiveActivity');
};

const PomodoroContext = createContext();

export const usePomodoro = () => {
  const context = useContext(PomodoroContext);
  if (!context) {
    throw new Error('usePomodoro must be used within PomodoroProvider');
  }
  return context;
};

export const PomodoroProvider = ({ children }) => {
  const { user, archiveTask } = useApp();
  
  // Debug logging
  const log = (msg, data) => console.log(`[Pomodoro] ${msg}`, data || '');
  
  // Settings
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Timer state
  const [mode, setMode] = useState(TIMER_MODES.WORK);
  const [timeRemaining, setTimeRemaining] = useState(DEFAULT_SETTINGS.workDuration * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionsCompleted, setSessionsCompleted] = useState(0);
  
  // Active task
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [activeTaskTitle, setActiveTaskTitle] = useState('');
  
  // Daily tracking
  const [todayFocusMinutes, setTodayFocusMinutes] = useState(0);
  const [todayDate, setTodayDate] = useState(new Date().toDateString());
  
  // Completion modal
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completedSession, setCompletedSession] = useState(null);
  
  // Refs
  const intervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const lastTickRef = useRef(Date.now());
  const hasLoadedRef = useRef(false);
  
  // Load settings from Firestore - ONLY ONCE per user session
  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.userId) {
        log('No user, skipping load');
        return;
      }
      
      // Prevent re-loading if already loaded for this user
      if (hasLoadedRef.current) {
        log('Already loaded settings, skipping re-load');
        return;
      }
      
      log('Loading settings for user:', user.userId);
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.userId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          log('Firestore data loaded:', { hasPomodoroSettings: !!data.pomodoroSettings, hasPomodoroState: !!data.pomodoroState });
          
          if (data.pomodoroSettings) {
            setSettings(prev => ({ ...prev, ...data.pomodoroSettings }));
          }
          if (data.pomodoroState) {
            // Restore persisted timer state
            const state = data.pomodoroState;
            log('Restoring state:', state);
            if (state.mode) setMode(state.mode);
            if (state.timeRemaining !== undefined) setTimeRemaining(state.timeRemaining);
            if (state.isRunning !== undefined) {
              log('Setting isRunning to:', state.isRunning);
              setIsRunning(state.isRunning);
            }
            if (state.sessionsCompleted !== undefined) setSessionsCompleted(state.sessionsCompleted);
            if (state.activeTaskId) setActiveTaskId(state.activeTaskId);
            if (state.activeTaskTitle) setActiveTaskTitle(state.activeTaskTitle);
            if (state.todayFocusMinutes !== undefined) {
              // Only restore if same day
              if (state.todayDate === new Date().toDateString()) {
                setTodayFocusMinutes(state.todayFocusMinutes);
                setTodayDate(state.todayDate);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to load pomodoro settings:', error);
      } finally {
        log('Settings loaded, setting settingsLoaded=true');
        hasLoadedRef.current = true;
        setSettingsLoaded(true);
      }
    };
    
    // Reset the loaded flag when user changes (logout/login)
    if (!user?.userId) {
      hasLoadedRef.current = false;
    }
    
    loadSettings();
  }, [user?.userId]);
  
  // Persist timer state to Firestore (debounced)
  const persistTimerState = useCallback(async () => {
    if (!user?.userId || !settingsLoaded) {
      log('Skipping persist:', { userId: user?.userId, settingsLoaded });
      return;
    }
    
    const stateToSave = {
      mode,
      timeRemaining,
      isRunning,
      sessionsCompleted,
      activeTaskId,
      activeTaskTitle,
      todayFocusMinutes,
      todayDate,
      lastUpdated: new Date().toISOString(),
    };
    log('Persisting state:', stateToSave);
    
    try {
      await setDoc(doc(db, 'users', user.userId), {
        pomodoroState: stateToSave
      }, { merge: true });
    } catch (error) {
      console.error('Failed to persist timer state:', error);
    }
  }, [user?.userId, settingsLoaded, mode, timeRemaining, isRunning, sessionsCompleted, activeTaskId, activeTaskTitle, todayFocusMinutes, todayDate]);
  
  // Debounced persist
  useEffect(() => {
    if (!settingsLoaded) return;
    const timer = setTimeout(persistTimerState, 1000);
    return () => clearTimeout(timer);
  }, [persistTimerState, settingsLoaded]);
  
  // Get audio context lazily
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);
  
  // Cleanup
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);
  
  // Reset daily tracking at midnight
  useEffect(() => {
    const today = new Date().toDateString();
    if (today !== todayDate) {
      setTodayFocusMinutes(0);
      setTodayDate(today);
    }
  }, [todayDate]);
  
  // Get duration for mode
  const getDurationForMode = useCallback((timerMode) => {
    switch (timerMode) {
      case TIMER_MODES.WORK: return settings.workDuration * 60;
      case TIMER_MODES.SHORT_BREAK: return settings.shortBreakDuration * 60;
      case TIMER_MODES.LONG_BREAK: return settings.longBreakDuration * 60;
      default: return settings.workDuration * 60;
    }
  }, [settings]);
  
  // Play notification sound - pleasant 4-note chime
  const playNotification = useCallback(() => {
    if (!settings.soundEnabled) return;
    
    try {
      const audioContext = getAudioContext();
      const now = audioContext.currentTime;
      
      const playTone = (frequency, startTime, duration, volume = 0.25) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, startTime);
        
        // Smooth envelope for pleasant sound
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.03);
        gainNode.gain.setValueAtTime(volume, startTime + duration * 0.5);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      // Pleasant 4-note ascending chime (C5 - E5 - G5 - C6)
      playTone(523.25, now, 0.25, 0.2);        // C5
      playTone(659.25, now + 0.2, 0.25, 0.22); // E5
      playTone(783.99, now + 0.4, 0.25, 0.24); // G5
      playTone(1046.5, now + 0.6, 0.4, 0.25);  // C6 (longer, slightly louder)
    } catch (error) {
      // Ignore audio errors
    }
  }, [settings.soundEnabled, getAudioContext]);
  
  // Show browser notification
  const showNotification = useCallback((title, body) => {
    if (settings.notificationsEnabled && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'pomodoro',
          renotify: true,
        });
      }
    }
  }, [settings.notificationsEnabled]);
  
  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);
  
  // Handle timer completion
  const handleTimerComplete = useCallback(() => {
    playNotification();
    
    // End Live Activity when session completes
    endLiveActivity();
    
    if (mode === TIMER_MODES.WORK) {
      const newSessionCount = sessionsCompleted + 1;
      setSessionsCompleted(newSessionCount);
      setTodayFocusMinutes(prev => prev + settings.workDuration);
      
      // Save session
      const session = {
        completedAt: new Date().toISOString(),
        duration: settings.workDuration,
        taskId: activeTaskId,
        taskTitle: activeTaskTitle,
      };
      setCompletedSession(session);
      
      // Determine next mode
      const isLongBreak = newSessionCount % settings.sessionsBeforeLongBreak === 0;
      const nextMode = isLongBreak ? TIMER_MODES.LONG_BREAK : TIMER_MODES.SHORT_BREAK;
      
      // Show completion modal (instead of auto-transitioning)
      setShowCompletionModal(true);
      
      showNotification(
        'Focus session complete',
        isLongBreak ? 'Time for a long break!' : 'Time for a short break!'
      );
      
      // Prepare next mode
      setMode(nextMode);
      setTimeRemaining(getDurationForMode(nextMode));
      setIsRunning(false);
      
    } else {
      // Break completed
      showNotification(
        '⏰ Break over!',
        'Ready to focus again?'
      );
      
      setMode(TIMER_MODES.WORK);
      setTimeRemaining(getDurationForMode(TIMER_MODES.WORK));
      
      if (settings.autoStartWork) {
        setIsRunning(true);
      } else {
        setIsRunning(false);
      }
    }
  }, [mode, sessionsCompleted, settings, activeTaskId, activeTaskTitle, playNotification, showNotification, getDurationForMode]);
  
  // Timer tick - use refs to avoid stale closures and ensure exact 1-second updates
  const timerStartRef = useRef(null);
  const startRemainingRef = useRef(null);
  
  useEffect(() => {
    if (!isRunning) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      timerStartRef.current = null;
      startRemainingRef.current = null;
      return;
    }
    
    // Capture start time and remaining time when timer starts
    if (timerStartRef.current === null) {
      timerStartRef.current = Date.now();
      startRemainingRef.current = timeRemaining;
    }
    
    intervalRef.current = setInterval(() => {
      // Calculate elapsed time since timer started for drift correction
      const elapsed = Math.floor((Date.now() - timerStartRef.current) / 1000);
      const newTime = Math.max(0, startRemainingRef.current - elapsed);
      
      setTimeRemaining(newTime);
      
      if (newTime === 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        timerStartRef.current = null;
        startRemainingRef.current = null;
        setIsRunning(false);
        setTimeout(handleTimerComplete, 0);
      }
    }, 1000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning, handleTimerComplete]);
  
  // Live Activity sync effect (update Dynamic Island every 5 seconds)
  const liveActivitySyncRef = useRef(null);
  const lastSyncTimeRef = useRef(0);
  
  useEffect(() => {
    if (!isRunning) {
      // Clear sync interval when paused
      if (liveActivitySyncRef.current) {
        clearInterval(liveActivitySyncRef.current);
        liveActivitySyncRef.current = null;
      }
      return;
    }
    
    // Sync immediately on start
    updateLiveActivity(timeRemaining, true, mode);
    lastSyncTimeRef.current = timeRemaining;
    
    // Sync every 5 seconds while running
    liveActivitySyncRef.current = setInterval(() => {
      // Calculate current time remaining based on last known value
      const elapsedSinceLastSync = 5;
      const currentTime = Math.max(0, lastSyncTimeRef.current - elapsedSinceLastSync);
      
      updateLiveActivity(currentTime, true, mode);
      lastSyncTimeRef.current = currentTime;
      
      log('Live Activity synced:', currentTime);
    }, 5000);
    
    return () => {
      if (liveActivitySyncRef.current) {
        clearInterval(liveActivitySyncRef.current);
        liveActivitySyncRef.current = null;
      }
    };
  }, [isRunning, mode]);
  
  // Update Live Activity when timeRemaining changes significantly (for accuracy)
  useEffect(() => {
    if (isRunning) {
      lastSyncTimeRef.current = timeRemaining;
    }
  }, [timeRemaining, isRunning]);

  // Ref for Live Activity sync interval (every 5 seconds)
  // (moved to above)
  
  // Controls
  const start = useCallback(() => {
    log('START called, setting isRunning=true');
    requestNotificationPermission();
    setIsRunning(true);
    
    // Start Live Activity (Dynamic Island)
    const totalDuration = getDurationForMode(mode);
    startLiveActivity(mode, totalDuration, activeTaskTitle, timeRemaining);
  }, [requestNotificationPermission, mode, activeTaskTitle, timeRemaining, getDurationForMode]);
  
  const pause = useCallback(() => {
    setIsRunning(false);
    
    // Pause Live Activity
    pauseLiveActivity(timeRemaining, mode);
  }, [timeRemaining, mode]);
  
  const reset = useCallback(() => {
    setIsRunning(false);
    setTimeRemaining(getDurationForMode(mode));
    
    // End Live Activity when reset
    endLiveActivity();
  }, [mode, getDurationForMode]);
  
  const skip = useCallback(() => {
    setIsRunning(false);
    handleTimerComplete();
    
    // End Live Activity when skipped
    endLiveActivity();
  }, [handleTimerComplete]);
  
  const switchMode = useCallback((newMode) => {
    setIsRunning(false);
    setMode(newMode);
    setTimeRemaining(getDurationForMode(newMode));
  }, [getDurationForMode]);
  
  const setTask = useCallback((taskId, taskTitle) => {
    setActiveTaskId(taskId);
    setActiveTaskTitle(taskTitle);
  }, []);
  
  const clearTask = useCallback(() => {
    setActiveTaskId(null);
    setActiveTaskTitle('');
    
    // End Live Activity when task cleared
    endLiveActivity();
  }, []);
  
  const updateSettings = useCallback(async (newSettings) => {
    setSettings(newSettings);
    
    if (user?.userId) {
      try {
        await setDoc(doc(db, 'users', user.userId), {
          pomodoroSettings: newSettings
        }, { merge: true });
      } catch (error) {
        console.error('Failed to save settings:', error);
      }
    }
    
    // Update current timer if not running
    if (!isRunning) {
      setTimeRemaining(getDurationForMode(mode));
    }
  }, [user?.userId, isRunning, mode, getDurationForMode]);
  
  // Complete task from modal
  const completeActiveTask = useCallback(() => {
    if (activeTaskId && archiveTask) {
      archiveTask(activeTaskId);
      clearTask();
    }
    setShowCompletionModal(false);
  }, [activeTaskId, archiveTask, clearTask]);
  
  // Dismiss modal and start break (always starts timer - user explicitly clicked the button)
  const startBreak = useCallback(() => {
    setShowCompletionModal(false);
    setIsRunning(true);
  }, []);
  
  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate progress
  const totalDuration = getDurationForMode(mode);
  const progress = totalDuration > 0 ? (totalDuration - timeRemaining) / totalDuration : 0;
  
  // Check if timer is active (running or paused mid-session)
  const isActive = isRunning || timeRemaining < totalDuration;
  
  
  const value = {
    // State
    settings,
    mode,
    timeRemaining,
    isRunning,
    sessionsCompleted,
    activeTaskId,
    activeTaskTitle,
    todayFocusMinutes,
    settingsLoaded,
    showCompletionModal,
    completedSession,
    
    // Computed
    formattedTime: formatTime(timeRemaining),
    progress,
    modeLabel: MODE_LABELS[mode],
    isActive,
    
    // Actions
    start,
    pause,
    reset,
    skip,
    switchMode,
    setTask,
    clearTask,
    updateSettings,
    requestNotificationPermission,
    completeActiveTask,
    startBreak,
    setShowCompletionModal,
  };
  
  return (
    <PomodoroContext.Provider value={value}>
      {children}
    </PomodoroContext.Provider>
  );
};
